/// <amd-module name="Pipeline/Analytics/PipelineOutcome/FailedRunsCard/ControllerViews/PipelineOutcomeFailedRunsCard" />
import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { IReportContext, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { ChartLegendComponent, IChartLegend, IChartLegendProps } from "Analytics/Shared/Visuals/ChartLegend/ChartLegend";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import { colors } from "Pipeline/Analytics/Common/Constants";
import { maxItemsToShowInCharts } from "Pipeline/Analytics/Common/Constants";
import { IStageMetrics } from "Pipeline/Analytics/Common/Types";
import { PipelineOutcomeFailedRunsActionsCreator } from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Actions/PipelineOutcomeFailedRunsActionsCreator";
import * as Resources from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Resources";
import {
    IPipelineOutcomeFailedRunsState,
    PipelineOutcomeFailedRunsStore
} from "Pipeline/Analytics/PipelineOutcome/FailedRunsCard/Stores/PipelineOutcomeFailedRunsStore";
import { PipelineOutcomeFailingTaskGridActionsCreator } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Actions/PipelineOutcomeFailingTaskGridActionsCreator";
import { PipelineOutcomeFailingTaskGrid } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/ControllerViews/PipelineOutcomeFailingTaskGrid";
import { PipelineOutcomeFailingTaskGridStore } from "Pipeline/Analytics/PipelineOutcome/FailingTaskGrid/Stores/PipelineOutcomeFailingTaskGridStore";
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import { Chart } from "VSS/Features/Charts/Chart";
import { Datum } from "VSS/Features/Charts/Contracts";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Components/Card/Card";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { getStackedBarChartProps } from "../Common/StackedBarChartDataSelector";
import { getStackedColumnChartProps } from "../Common/StackedColumnChartDataSelector";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export interface IPipelineOutcomeFailedRunsCardProps extends IVssComponentProperties {
    failingTaskGridStore: PipelineOutcomeFailingTaskGridStore;
    store: PipelineOutcomeFailedRunsStore;
    failingTaskGridActionsCreator: PipelineOutcomeFailingTaskGridActionsCreator;
    actionCreator: PipelineOutcomeFailedRunsActionsCreator;
    headerClassName?: string;
    onDataRendered?: () => void;
    errorText?: string;
    reportContext: IReportContext;
}

enum WidgetCharts {
    Chart_FailedRunsReport="Chart_FailedRunsReport",
    SummaryChart_FailedRunsReport="SummaryChart_FailedRunsReport"
}

export class PipelineOutcomeFailedRunsCard extends VssComponent<IPipelineOutcomeFailedRunsCardProps, IPipelineOutcomeFailedRunsState> {
    private _onDataRenderedCalled = false;
    private isChartClicked = {
        [WidgetCharts.Chart_FailedRunsReport]: false,
        [WidgetCharts.SummaryChart_FailedRunsReport]: false
    }

    constructor(props: IPipelineOutcomeFailedRunsCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<IPipelineOutcomeFailedRunsState>;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IPipelineOutcomeFailedRunsCardProps, prevState: IPipelineOutcomeFailedRunsState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
        if (this.state.confValues && !prevState.confValues && !this.state.stages) {
            this.props.actionCreator.initializeStages(this.props.reportContext, { period: Period.Days_30, trendBy: TrendBy.Days });
        }
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render(): JSX.Element | null {
        let title = Resources.CardTitle;
        if (
            this.state.data &&
            this.state.data[0] &&
            this.state.data[0].stage &&
            this.state.data[0].stage.name == Resources.AllStageName &&
            this.state.data[0].stage.dataCount === 0
        ) {
            return null;
        }

        if (this.state.stages) {
            if (this.state.stages.length > 1) {
                title = localeFormat(Resources.CardTitleForStage, this.getSelectedStageName().toLowerCase());
            }
        }
        return (
            <Card
                className={this.props.className}
                titleProps={{ text: title, ariaLevel: 2 }}
                headerClassName={this.props.headerClassName}
                headerCommandBarItems={this._getHeaderCommandBarItems()}
            >
                {this._getElementToDisplay()}
            </Card>
        );
    }

    private _getHeaderCommandBarItems = (): IHeaderCommandBarItem[] => {
        const commandBarItems: IHeaderCommandBarItem[] = [];
        const subMenuItems: IMenuItem[] = [];
        if (this.state.stages) {
            this.state.stages.forEach(stage => {
                subMenuItems.push({
                    id: stage.fullName,
                    text: stage.name,
                    onActivate: () => {
                        this._onStageChanged(stage);
                    }
                });
            });
        }

        if (subMenuItems.length > 1) {
            commandBarItems.push({
                id: "pipelineStagesCommandBarButton",
                text: this.getSelectedStageName(),
                important: true,
                subMenuProps: {
                    id: "stageSubMenu",
                    items: subMenuItems
                }
            });
        }
        return commandBarItems;
    };

    private _onStageChanged = (stage: IStageMetrics): void => {
        this.props.actionCreator.selectStage(stage.fullName);
        this.state.confValues!.stage = stage;
        this.props.failingTaskGridActionsCreator.updateConfigurationValues(this.props.reportContext, this.state.confValues!);
    };

    private getSelectedStageName() {
        if (this.state.stages && this.state.stages.length > 1) {
            return this.state.stages.filter(s => {
                if (s.fullName === this.state.selectedStageFullName) {
                    return s;
                }
            })[0].name;
        }
        return "";
    }

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
    }

    private _getElementToDisplay(): JSX.Element {
        if (this.state.errorText) {
            return <ErrorComponent className="pipeline-outcome-failedruns-container flex-grow" errorMessage={this.state.errorText} />;
        } else if (this.state.data) {
            const filteredData = this.state.filteredData;
            const topFailingTasks = filteredData ? filteredData.topFailingTasks : undefined;
            if (!topFailingTasks || topFailingTasks.failedCount === 0) {
                return <AnalyticsUnavailableMessage suggestion={Resources.NoTaskFailures} />;
            } else {
                const chartProps = getStackedColumnChartProps(this.context.pageContext, filteredData.trendChartData, this.state.confValues);
                chartProps.click = () => {
                    if(!this.isChartClicked[WidgetCharts.Chart_FailedRunsReport]){
                        this.publishWidgetChartClickTelemetry(WidgetCharts.Chart_FailedRunsReport);
                    }
                    this.isChartClicked[WidgetCharts.Chart_FailedRunsReport] = true;
                }
                const summaryChartProps = getStackedBarChartProps(this.context.pageContext, topFailingTasks);
                summaryChartProps.click = () => {
                    if(!this.isChartClicked[WidgetCharts.SummaryChart_FailedRunsReport]){
                        this.publishWidgetChartClickTelemetry(WidgetCharts.SummaryChart_FailedRunsReport);
                    }
                    this.isChartClicked[WidgetCharts.SummaryChart_FailedRunsReport] = true;
                }

                const legendData = this._getLegendsData();
                const failedTaskCount = topFailingTasks.failingTasksData.length;
                const topNTaskFailedCount = topFailingTasks.failingTasksData.slice(0, maxItemsToShowInCharts).reduce((accumulator, failingtask) => {
                    return accumulator + failingtask.failedCount;
                }, 0);
                const top10TaskContribution = getPercentage(topFailingTasks.failedCount, topNTaskFailedCount);
                return (
                    <div className="pipeline-outcome-failedruns-container flex-grow">
                        <div className="topfailingtask-header font-size-m font-weight-semibold">
                            {localeFormat(Resources.TopNFailedTask, maxItemsToShowInCharts)}
                        </div>
                        <div className="flex-row flex-wrap">
                            <div className="pipeline-failedruns-summary flex-column">
                                <ChartLegendComponent {...this._getLegendProps(legendData)} />
                                <div className="pipeline-failedruns-insight">
                                    {failedTaskCount == 1
                                        ? Resources.InsightMessageWith1TaskFailure
                                        : localeFormat(
                                              Resources.InsightMessage,
                                              failedTaskCount < maxItemsToShowInCharts ? failedTaskCount : maxItemsToShowInCharts,
                                              top10TaskContribution
                                          )}
                                </div>
                                <div className={this.props.chartContainerClassName || ""}>
                                    <Chart {...summaryChartProps} />
                                </div>
                            </div>
                            <div className="pipeline-failedruns-trend">
                                <div className={this.props.chartContainerClassName || ""}>
                                    <Chart {...chartProps} />
                                </div>
                            </div>
                        </div>
                        <div>
                            <PipelineOutcomeFailingTaskGrid
                                className="pipeline-task-failure-grid-container flex-grow flex-column"
                                store={this.props.failingTaskGridStore}
                                actionCreator={this.props.failingTaskGridActionCreator}
                                reportContext={this.props.reportContext}
                            />
                        </div>
                    </div>
                );
            }
        } else {
            return <Spinner className={css("analytics-card-spinner flex-grow flex-column", this.props.className || "")} size={SpinnerSize.large} />;
        }
    }

    private _getLegendsData(): Datum[] {
        const chartData: Datum[] = [];
        if (this.state.filteredData) {
            let i = 0;
            this.state.filteredData.topFailingTasks.failingTasksData.forEach(failingTask => {
                chartData.push({
                    name: failingTask.taskName,
                    color: colors[i++]
                } as Datum);
            });
        }
        return chartData;
    }

    private _getLegendProps(chartData: Datum[]): IChartLegendProps {
        const className = css("flex-grow");
        const legendRows = this._getLegendRows(chartData);

        return {
            className: className,
            legend: legendRows
        };
    }

    private _getLegendRows(chartData: Datum[]): IChartLegend[] {
        return chartData
            ? chartData
                  .filter(o => o.y !== 0)
                  .map((o, i) => {
                      return {
                          color: o.color,
                          label: o.name
                      } as IChartLegend;
                  })
            : [];
    }

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };

    private publishWidgetChartClickTelemetry = (chartType: string) => {
        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("PipelineAnalytics", "WidgetChartClick", {
            Chart: chartType
        })
    }
}
